import React from "react";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import ReactDOM from "react-dom";
import { commitLocalUpdate, commitMutation, graphql, type RelayRefetchProp } from "react-relay";
import { type RouteComponentProps, withRouter } from "react-router";

import { withProfiler } from "@sentry/react";
import cx from "classnames";
import fastdom from "fastdom";
import { compact, debounce, isEqual, sortBy, uniqBy } from "lodash";
import moment from "moment";
import { EditorState } from "prosemirror-state";
import invariant from "tiny-invariant";
import useResizeObserver from "use-resize-observer";

import { NoteBlockStatus } from "~/actionItems/enum";
import {
	AGENDA_BUILDER_FEATURE_FLAG,
	AgendaBuilderBanner,
	ErrorBanner,
	type HeaderContentType as GeneratedHeaderContentType,
} from "~/agendaBuilder";
import AgendaGenerator from "~/agendaGenerator";
import Attachments, { AttachmentsRelay } from "~/attachments/AttachmentsRelay";
import { NoteAndFeedbackHistoryPaywall, showFeatureModal } from "~/billing";
import type { AttendeeAttrs, AttendeesPresenceInfo } from "~/common/Attendees";
import { DismissibleElementsContext } from "~/common/DismissibleElements";
import Fullscreen from "~/common/Fullscreen";
import ViewportContext from "~/common/ViewportContext";
import MeetingFeedbackModal, { type Props as MeetingFeedbackModalProps } from "~/feedback/MeetingFeedbackModal";
import type { IntegrationProvider } from "~/integrations/actionItemIntegration";
import { TeamsContext } from "~/integrations/teams/teamsContext";
import growthbook, { trackConversion } from "~/lib/ab/growthbook";
import { inVideocall } from "~/lib/browserExtension";
import { fetchFeature } from "~/lib/flippy";
import { SetOuterObjectContext } from "~/lib/outerObjectContext";
import { warning } from "~/lib/prompts";
import { getAllMarks } from "~/lib/prose/marks";
import {
	CustomEmoji,
	Embed,
	HardBreak,
	HorizontalRule,
	KeyResult,
	Mention,
	Tag,
	TextNode,
	Unstyled,
} from "~/lib/prose/nodes";
import { Header as HeaderNode } from "~/lib/prose/nodes/header";
import { ImageStrip, ImageStripItem, ImageStripItemCaption, ImageStripItemFile } from "~/lib/prose/nodes/image-strip";
import { BulletPoint, NumberedListItem } from "~/lib/prose/nodes/plaintext";
import ActionItemIntegrationsPlugin, {
	proseIntegrationKey as actionItemIntegrationLinkProseKey,
} from "~/lib/prose/plugins/actionItemIntegrationLink";
import ActionItemProgressPlugin from "~/lib/prose/plugins/actionItemProgress";
import { aiProseKey, AITopicsExtension } from "~/lib/prose/plugins/aiTopics";
import AsanaItems, {
	proseIntegrationKey as asanaItemsProseKey,
	type AsanaItemsUpdateData,
	type AsanaUpdateType,
} from "~/lib/prose/plugins/asanaItems";
import { Agenda, Goal, Todo } from "~/lib/prose/plugins/assignable";
import { BlockActions } from "~/lib/prose/plugins/blockActions";
import BlockAnnotation from "~/lib/prose/plugins/blockAnnotation";
import BlockBoundary, { getBlockBoundaryState } from "~/lib/prose/plugins/blockBoundary";
import BlockCreator from "~/lib/prose/plugins/blockCreator";
import Collab, { CollabPluginKey, type StatusChange } from "~/lib/prose/plugins/collab";
import { CollapseExtension, getCollapsedAncestors, setCollapsedMany } from "~/lib/prose/plugins/collapse";
import Comments, { type CommentUpdateData } from "~/lib/prose/plugins/comments";
import DropTarget, {
	FellowAttachmentHandler,
	FileDropHandler,
	ImageStripItemHandler,
	NoteTaskDropHandler,
} from "~/lib/prose/plugins/dropTarget";
import EmojiProvider, { PasteBuiltInEmoji } from "~/lib/prose/plugins/emoji";
import ExternalAttribute from "~/lib/prose/plugins/externalAttribute";
import FilePaste from "~/lib/prose/plugins/filePaste";
import FocusArea from "~/lib/prose/plugins/focusArea";
import Indent from "~/lib/prose/plugins/indent";
import InputRules from "~/lib/prose/plugins/inputRules";
import { Keyboard } from "~/lib/prose/plugins/keyboard";
import MentionProvider from "~/lib/prose/plugins/mention";
import ModS from "~/lib/prose/plugins/modS";
import MousePos from "~/lib/prose/plugins/mousePos";
import MoveSelection from "~/lib/prose/plugins/moveSelection";
import MultiCursor, { type UserPresence } from "~/lib/prose/plugins/multicursor";
import MultiSelect, { MultiSelectDropHandler } from "~/lib/prose/plugins/multiselect";
import Paste from "~/lib/prose/plugins/paste";
import { Placeholder } from "~/lib/prose/plugins/placeholder";
import { PromptExtension } from "~/lib/prose/plugins/prompt";
import Reactions, { type ReactionUpdateData } from "~/lib/prose/plugins/reactions";
import RecordVideo from "~/lib/prose/plugins/recordVideo/recordVideo";
import SelectedBlock from "~/lib/prose/plugins/selectedBlock";
import SlashProvider from "~/lib/prose/plugins/slash";
import TagProvider from "~/lib/prose/plugins/tag";
import Timestamp, { withTimestampAttrs } from "~/lib/prose/plugins/timestamp/timestamp";
import Toolbar from "~/lib/prose/plugins/toolbar";
import TypeAhead from "~/lib/prose/plugins/typeahead";
import ProseReact, { EditorConfig } from "~/lib/prose/prose-react";
import { decodeGlobalId, smart } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { fromEntries, inCalendarMode, inVideocallMode, isNative } from "~/lib/utils";
import WebsocketClient, { GroupEmitter } from "~/lib/websocket";
import type { ChaptersPanelRef } from "~/meetingRecorder/ChaptersPanel";
import { AskFellowActionProvider } from "~/meetingRecorder/RecordingControls/AskFellowActionContext";
import { onTimestampClickUtil } from "~/meetingRecorder/useOnTimestampClick";
import { openRecapModal } from "~/meetings/MeetingModal/openRecapModal";
import { environment } from "~/models";
import type { TalkingPoint } from "~/notes/PendingBlocksModal";
import { cache as jumpInfoCache } from "~/notes/useActionItemJump";
import { ApplyTemplateModal } from "~/noteTemplate/ApplyModal";
import { NoteTemplateModalOpener, type NoteTemplateModalOpenerRef } from "~/noteTemplate/modal";
import { applyTemplate } from "~/noteTemplate/noteTemplateGql";
import type { DeletionType } from "~/stream/DeleteNoteModal/DeleteNoteModal";
import EmployeeGuidance, { type Suggestion } from "~/stream/EmployeeGuidance";
import MeetingRecap, { type MeetingRecapRef } from "~/stream/MeetingContainer/MeetingRecap";
import type { CommentContext } from "~/stream/SideBar/SideBarContext";
import {
	CancelMeeting,
	NoteLocked,
	NoteReadOnly,
	RequestCancelMeeting,
	TimeSaver,
} from "~/stream/StreamSettings/Automations/Banners";
import { __globalModalContext } from "~/ui/modals";
import toast from "~/ui/toast";
import { useHideDuringAndAfterTour } from "~/welcomeChecklist";

import AgendaGeneratorPreview from "../agendaGenerator/AgendaGeneratorPreview";
import { RECORDING_SHOW_VIDEO, type RecordingShowVideoEvent } from "../meetingRecorder/constants";
import FloatingRecordingControls, {
	FLOATING_RECORDING_CONTROLS_FEATURE,
} from "../meetingRecorder/RecordingControls/FloatingRecordingControls";
import EventFormModal from "../nav/panels/Meetings/EventFormModal/EventFormModal";
import { eventEmitter } from "./Comments/events";
import ResolvedModal from "./Comments/ResolvedModal";
import { threadKeyFromNoteIdAndBlockKey, toPathForTab } from "./Comments/threadKeys";
import { LiveReactionContainer } from "./LiveReactions";
import MeetingContainerTabs, { type CurrentTabType } from "./MeetingContainer/MeetingContainerTabs";
import { MeetingPhaseContextProvider } from "./MeetingPhaseContext";
import NoteHeader from "./NoteHeader/NoteHeader";
import OutOfCreditsModal from "./NoteHeader/OutOfCreditsModal";
import { watchStream } from "./StreamHeader/moreActions/WatchStream";
import SuggestedActions from "./SuggestedActions";
import { baseStreamUrl, formatLastUpdatedDate, noteContainsStreamTemplateOnly, noteIsEmpty } from "./utils";

import type { WelcomeChecklistTaskKey } from "~/__generated__/ChecklistCompleteTaskMutation.graphql";
import type {
	AgendaBuilderTone,
	ConferenceCallProvider,
	NoteCard_note$data as Note,
} from "~/__generated__/NoteCard_note.graphql";
import type { NoteCard_viewer$data as Viewer } from "~/__generated__/NoteCard_viewer.graphql";
import type { NoteCardAddBlocksMutation as AddBlocksMutationType } from "~/__generated__/NoteCardAddBlocksMutation.graphql";
import type { NoteCardDismissGuidanceMutation as DismissGuidanceType } from "~/__generated__/NoteCardDismissGuidanceMutation.graphql";
import type { NoteCardGenerateAgendaHeadersMutation as GenerateAgendaHeadersType } from "~/__generated__/NoteCardGenerateAgendaHeadersMutation.graphql";
import type { NoteCardSplitMutation as SplitMutationType } from "~/__generated__/NoteCardSplitMutation.graphql";
import type { NoteCardUpdateMutation as UpdateMutationType } from "~/__generated__/NoteCardUpdateMutation.graphql";

import styles from "./css/NoteCard.module.css";

function StickyNoteBanners({
	children,
	onHeightChanged,
}: {
	children: React.ReactNode;
	onHeightChanged: (height: number) => void;
}) {
	const { hiddenStyles } = useHideDuringAndAfterTour();

	const { ref: resizeRef } = useResizeObserver({
		onResize({ height }) {
			onHeightChanged(height ?? 0);
		},
	});

	return (
		<div ref={resizeRef} className={styles.stickyNoteBanners} style={hiddenStyles}>
			{children}
		</div>
	);
}

export const CUSTOMIZE_SETTINGS_ILLUSTRATION = "CarryForwardBanners__customizeSettings";

export const updateMutation = graphql`
	mutation NoteCardUpdateMutation($input: UpdateNoteInput!) {
		updateNote(input: $input) {
			clientMutationId
			ok
			note {
				id
				finished
				title
				eventId
				eventGuid
				eventTitle
				eventStart
				eventEnd
				isLocked
				stream {
					displayName
				}

				event {
					id
					start
					end
					title
					guid
					conferenceCalls {
						provider
						url
						phoneNumber
						pin
					}
					isOrganizer
					userResponseStatus
				}
			}
		}
	}
`;

const addBlocksMutation = graphql`
	mutation NoteCardAddBlocksMutation($input: AddBlocksInput!) {
		addBlocks(input: $input) {
			ok
		}
	}
`;

const dismissGuidanceMutation = graphql`
	mutation NoteCardDismissGuidanceMutation($input: DismissGuidanceInput!) {
		noteStreamDismissGuidance(input: $input) {
			ok
		}
	}
`;

const splitMutation = graphql`
	mutation NoteCardSplitMutation($input: SplitNoteInput!) {
		splitNote(input: $input) {
			clientMutationId
			ok
			note {
				id
				stream {
					...stream_mainstream
				}
			}
		}
	}
`;

const generateAgendaHeadersMutation = graphql`
	mutation NoteCardGenerateAgendaHeadersMutation($noteId: ID!) {
		generateAgendaHeaders(noteId: $noteId) {
			headers {
				id
				content
				subheader
				talkingPoint
			}
		}
	}
`;

export function updateNote(
	noteId: string,
	updates: {
		/* eslint-disable camelcase */
		finished?: boolean;
		event_guid?: string | null;
		event_title?: string;
		event_start: string | null;
		event_end: string | null;
		/* eslint-enable camelcase */
	},
	cb: (resp: UpdateMutationType["response"]) => void,
) {
	commitMutation<UpdateMutationType>(environment, {
		mutation: updateMutation,
		variables: {
			input: {
				noteId: noteId,
				updates: updates,
			},
		},
		onCompleted: resp => {
			cb && cb(resp);
		},
	});
}

const debouncedWatchStream = debounce(watchStream, 100);

export interface NoteCardRouteState {
	applyTemplateId?: string;
	latestNoteId?: string;
	currentTab?: CurrentTabType;
}

export type Props = RouteComponentProps<{}, {}, NoteCardRouteState> & {
	note: Note;
	viewer: Viewer;
	employeeGuidance?: Suggestion;
	employeeGuidanceShowManager?: boolean;
	inviteAllowed: boolean;
	onComponentUnmount?: () => void;
	onSplitNote?: () => void;
	onChange?: () => void;
	pendingTodoCount?: number;
	disableDateTimeSelector?: boolean;
	showUnlinkNote?: boolean;
	style?: React.CSSProperties;
	onOpenShareModal?: () => void;
	relay: RelayRefetchProp;
	isFirstNote: boolean;
	isNoteSpecifiedByUrl: boolean;
	onNewExternalsAvailable?: (externals: readonly AttendeeAttrs[]) => void;
	embedded?: boolean;
	showAutomationTimeSaverIllustration?: boolean;
	onShowFeedbackContent?: (id: string) => void;
	inAgendaBuilderMode: boolean;
	setInAgendaBuilderMode: (inAgendaBuilderMode: boolean) => void;
	onAgendaBuilderSettings?: () => void;
	agendaBuilderModeRegenerateKey?: number;
	initiallyHighlightedBlockKey?: string;
	isDarkMode: boolean;
	refetchStream: (refetchStreamNotes: boolean) => void;
	printProps: {
		inPrintNodeMode: boolean;
		isIsolatedNoteCard: boolean;
		isolateThisNote: () => void;
	};
	doneGeneratingAIHeaders?: boolean;
	setDoneGeneratingAIHeaders?: (done: boolean) => void;
	setCommentContext: (commentContext?: CommentContext) => void;
};

interface State {
	fullscreen: boolean;
	isOnScreen: boolean;
	/* States from prose-editor */
	attendeesPresenceInfo: AttendeesPresenceInfo;
	anonymousAttendees: AttendeeAttrs[];
	noteHasBeenEdited: boolean;
	isCrossWorkspaceEnabled: boolean;
	generatedHeaders: GeneratedHeaderContentType[];
	exitedAgendaBuilderMode: boolean;
	agendaBuilderError: boolean;
	noteHeaderHeight: number;
	stickyNoteBannersHeight: number;
	suggestedActionsHeight: number;
	currentTab: CurrentTabType;
	forwardedRecapVideoState?: { playing: boolean; currentTime: number };
	selectedRecordingId?: string;
}

export type EditorContextParticipant = AttendeeAttrs | Note["noteuserSet"][number]["user"];

interface EditorContext extends Record<string, unknown> {
	userId: string;
	isAccountAdmin: boolean;
	streamId: string;
	streamName: string;
	streamType: string;
	streamIsExternal: boolean;
	streamHasExternalUsers: boolean;
	noteId: string;
	noteUrl: string | null;
	noteEventGuid: string | null;
	billing: {
		hasAccessToDuedates: boolean;
		hasAccessToJiraIntegration: boolean;
		hasAccessToAsana: boolean;
		hasAccessToObjectives: boolean;
	};
	jira?: {
		id: string;
		clientKey: string;
		baseUrl: string;
	};
	asanaIsConnected?: boolean;
	connectedActionItemIntegrations: readonly IntegrationProvider[];
	participants: EditorContextParticipant[];
	hasAccessToOriginalBlock: Record<string, boolean>;
	hasAccessToVersioning: boolean;
	hasAccessToNoteLocking: boolean;
	hasAccessToPromptHeadings: boolean;
	getLastUpdatedString: () => string;
	onMeetingFeedbackClick?: () => void;
	meetingFeedbackSent: boolean;
	meetingFeedbackResponseCount: number;
	isFirstNote: boolean;
	isMobile: boolean;
	isDarkMode: boolean;
	/* States for AI Agenda Builder */
	generatedHeaders?: GeneratedHeaderContentType[];
	agendaBuilderTone?: AgendaBuilderTone;
	agendaBuilderContext?: string;
	/* END: States for AI Agenda Builder */
	dismissibleElementsContext: React.ContextType<typeof DismissibleElementsContext>;
}

export type NoteSocketPayload =
	| {
			type:
				| "ACTION_ITEMS_CARRIED_FORWARD"
				| "TALKING_POINTS_CARRIED_FORWARD"
				| "EVERYTHING_CARRIED_FORWARD"
				| "NOTE_UPDATED";
			data: Partial<Note>;
	  }
	| { type: "REACTIONS_CHANGED"; data: ReactionUpdateData }
	| { type: "COMMENTS_CHANGED"; data: CommentUpdateData }
	| { type: AsanaUpdateType; data: AsanaItemsUpdateData }
	| {
			type: "ACTION_ITEM_INTEGRATION_LINK_UPDATED";
			data: {
				blockId: string;
				blockKey: string;
			};
	  }
	| { type: "NOTE_CARRY_FORWARD"; data: { operation: string } }
	| { type: "AI_HEADERS_UPDATE"; data: { headerData: GeneratedHeaderContentType; status: "DONE" | "LOADING" } }
	| { type: "LIVE_REACTION"; data: { emojis: string[]; sender: string; senderId: string } };

graphql`
	fragment NoteCard_event on Event {
		id
		start
		end
		title
		guid
		type
		calendar {
			id
		}
		status
		conferenceCalls {
			provider
			url
			phoneNumber
			pin
		}
		location
		recurrenceRule
		userResponseStatus
		isHidden
		isOrganizer
		isAllDay

		attendees {
			__typename
			email
			invitable
			fullName
			image
			user {
				id
				isActive
				isConfigured
				isTopUser
			}
		}
	}
`;

export const DEFAULT_NOTE_HEADER_HEIGHT = 50;

export class DumbNoteCard extends React.PureComponent<Props, State> {
	static Fragments = {
		note: graphql`
			fragment NoteCard_note on Note {
				id
				createdAt
				updatedAt
				rawDraftJson
				eventTitle
				eventGuid
				eventStart
				eventEnd
				finished
				finishedAt
				isLocked
				canUnlockNote
				noteTitle: title
				schemaVersion
				noteType
				version
				url
				collapsedBlockKeys
				showAiAgendaBuilderFeatureToViewer
				eventAttachments {
					provider
					fileId
					url
					fileType
					title
				}

				linkedActionItems: actionItems(linkedOnly: true) {
					hasOriginalBlock
					originalBlockId
					originalBlock {
						key
						note {
							id
							stream {
								url
							}
						}
						# When action items are carried forward to a note that doesn't have
						# enough data in the relay store to render the action items panel,
						# there can be a crash when the NoteCardRefetchQuery is run.
						# Querying for all the data here sucks but it does fix it.
						...ActionItemData
					}
				}

				hasMeetingRecordings
				meetingRecordings {
					id
					isCurrentlyRecording
					transcript
				}

				...asanaItems @relay(mask: false)
				...actionItemIntegrationLink @relay(mask: false)
				...EditTemplateMenuItem_note

				stream {
					id
					watching
					displayName
					type
					typeTag
					templateHasBeenChanged
					carryTalkingPoints
					carryActionItems
					isMeetingFeedbackPromptEnabled
					isExternal
					hasExternalUsers
					fromSignupForViewer
					openActionItemCount
					agendaBuilderTone
					agendaBuilderContext
					schedulingMeetingAssistantAutomationIsActive
					account {
						id
						name
					}
					template {
						id
						upgradedProseDoc
					}
					notestreamusers {
						user {
							email
						}
					}
					otherUser {
						id
						email
						fullName
						image
						title
						department
						isActive
						isConfigured
						isGuest
						type
						relationshipToViewer
						activeRecurring1on1Event {
							start
							recurrenceRule
						}
						internalAttributes {
							suggestedTopics
							manager {
								id
								fullName
							}
						}
					}
				}

				noteuserSet {
					id
					lastSeenAt
					user {
						__typename
						id
						type
						email
						firstName
						fullName
						image
						isActive
						isConfigured
						isGuest
						isTopUser
						isExternal

						internalAttributes {
							invitable
						}
					}
				}
				externalUsers {
					__typename
					id
					type
					email
					firstName
					fullName
					image
					isActive
					isConfigured
					isGuest
					isTopUser
					isExternal

					internalAttributes {
						invitable
					}
				}

				event {
					...NoteCard_event @relay(mask: false)
					...AttachmentsOrConference_event
					...JoinConferenceButton_event
				}

				...AttachmentsRelay_note
				attachments {
					id
					dimensions {
						height
						width
					}
					...AttachmentPreviewAttachment
				}

				groupName
				actionItemsCarriedForward
				talkingPointsCarriedForward
				viewerDismissedCarryForwardActionItemBanner
				viewerDismissedCarryForwardTalkingPointBanner
				viewerAgendaGeneratorBannerDismissed
				viewerSentMeetingFeedback
				viewerCanEdit
				meetingFeedbackResponseCount
				viewerShowAutomationTimeSaverBanner
				previousNote {
					pendingTodoCount
					pendingAgendaCount
				}

				reactions

				comments {
					blockKey
					count
					users {
						id
						fullName
						image
						isExternal
					}
				}

				...FloatingRecordingControls_note
				...AskFellowHooks_useAskFellowButton_note
				...MeetingRecordingBanner_RecordMeetingButton_note
				...MeetingPhaseContext_note
				...MeetingSummaryBanner_note
				...MeetingSummaryBanner_IfMeetingSummaryBannerHidden_note
				...MeetingSummaryBanner_useShouldShowMeetingSummaryBanner_note
				...SendNotesModal_IncludeAISummaryFormField_note
				...SuggestedActions_note
				...SendNotesModal_note
				...TranscriptModal_note @arguments(public: false)
				...NoteHeader_note
				...MeetingContainerTabs_note
			}
		`,

		viewer: graphql`
			fragment NoteCard_viewer on Viewer {
				hasAccessToCopilot
				lastUsedBrowserExtension
				lastUsedZoomApp
				canCreateWorkspaceTemplate
				connectedActionItemIntegrations
				remainingMeetingRecordingCredits

				integrations {
					asana {
						isConnected
					}
					zoom {
						hasLocalRecordingTokenScope
					}
				}

				user {
					id
					type
					email
					firstName
					lastName
					fullName
					isGuest
					internalAttributes {
						isAccountAdmin
						manager {
							id
						}
					}
				}

				...EventsCalendars @relay(mask: false)

				settings {
					automaticallyWatchNotestreams
					managerDisabled
				}

				account {
					id
					billingAccount {
						hasAccessToDuedates: hasAccessToFeature(feature: duedates)
						hasAccessToVersioning: hasAccessToFeature(feature: versioning)
						hasAccessToSharing: hasAccessToFeature(feature: sharing)
						hasAccessToJiraIntegration: hasAccessToFeature(feature: jira_integration)
						hasAccessToComments: hasAccessToFeature(feature: comments)
						hasAccessToMeetingFeedback: hasAccessToFeature(feature: meeting_feedback)
						hasAccessToNoteLocking: hasAccessToFeature(feature: note_locking)
						hasAccessToEmbeds: hasAccessToFeature(feature: embeds)
						hasAccessToSalesforce: hasAccessToFeature(feature: salesforce_integration)
						hasAccessToHubspot: hasAccessToFeature(feature: hubspot_integration)
						hasAccessToAsana: hasAccessToFeature(feature: asana_integration)
						hasAccessToObjectives: hasAccessToFeature(feature: objectives)
						hasAccessToPromptHeadings: hasAccessToFeature(feature: prompt_headings)
						hasAccessToMultipleAssignees: hasAccessToFeature(feature: multiple_assignees)
						featureAccess
					}

					integrations {
						jira {
							id
							clientKey
							baseUrl
						}
					}

					isPersonalType
					emailDomainList
					loomEnabled
					allowExternalInviting
					allowCompleteByAny
					allowAiFeatures
					allowMeetingRecording
					allowRecapDownload
					primaryAdmin {
						fullName
					}
					tags {
						__typename
						id
						label
						color
					}
					defaultMeetingTemplate {
						id
						isWhiteListedForTemplateBanner
					}
					defaultOneononeTemplate {
						id
						isWhiteListedForTemplateBanner
					}
				}

				welcomeChecklist {
					firstAgendaPrepared: task(key: FIRST_AGENDA_PREPARED)
				}

				...PreMeetingControls_viewer
				...NoteHeader_viewer
				...EventFormModal_viewer
				...NoteAndFeedbackHistoryPaywall_viewer
				...BillingIdentityFeaturesPaywall_viewer
				...SendNotesModal_viewer
				...MeetingRecordingBanner_viewer
				...MeetingPhaseContext_viewer
				...SuggestedActions_viewer
				...JoinConferenceButton_viewer
				...LiveReactions_viewer
				...TranscriptModal_viewer @arguments(isPublic: false)
				videoPlaybackSettings {
					...TranscriptVideo_videoPlaybackSettings
				}
			}
		`,
	};

	static Refetch = graphql`
		query NoteCardRefetchQuery($id: ID!) {
			note: node(id: $id) {
				...NoteCard_note
			}
		}
	`;

	static contextType = TeamsContext;
	declare context: React.ContextType<typeof TeamsContext>;

	_isMounted = false;

	editorRef: React.RefObject<ProseReact> = React.createRef();
	containerRef: React.RefObject<HTMLDivElement> = React.createRef();
	noteTemplateModal = React.createRef<NoteTemplateModalOpenerRef>();
	meetingRecapRef = React.createRef<MeetingRecapRef>();
	videoPlayerRef: React.RefObject<HTMLVideoElement> = React.createRef();
	chaptersRef: React.RefObject<ChaptersPanelRef> = React.createRef();

	intersectionObserver: IntersectionObserver;

	attachmentDropzoneRef: AttachmentsRelay | null = null;
	attachmentsContainer?: HTMLElement;

	editorConfig: EditorConfig;
	prevEditorContext: EditorContext | undefined;

	collab: null | Collab = null;
	multicursor: null | MultiCursor = null;
	externalAttribute: null | ExternalAttribute = null;

	firstEditAlreadyHappened = false;
	alreadyTriedToWatch = false;
	meetingFeedbackModalOpened = false;
	copilotPaywallModalOpened = false;

	socket: GroupEmitter | null = null;

	debouncedUpdateExtension = debounce(this.updateExtension, 1000);

	collabLastSaved = 0;

	toolbarMountPointElementId: string;

	constructor(props: Props) {
		super(props);

		const { pathname, hash } = this.props.location;
		const isStreamUrl = pathname.includes("/stream/") && !hash.includes("/recording/");
		const tab =
			this.props.location.state?.currentTab ||
			(isStreamUrl ? "notes" : props.note.hasMeetingRecordings ? "recap" : "notes");

		this.state = {
			fullscreen: false,
			/* States from prose-editor */
			attendeesPresenceInfo: {},
			anonymousAttendees: [],
			noteHasBeenEdited: false,
			isCrossWorkspaceEnabled: false,
			isOnScreen: true,
			generatedHeaders: [],
			exitedAgendaBuilderMode: false,
			agendaBuilderError: false,
			noteHeaderHeight: DEFAULT_NOTE_HEADER_HEIGHT,
			stickyNoteBannersHeight: 0,
			suggestedActionsHeight: 0,
			currentTab: tab as CurrentTabType,
		};

		this.toolbarMountPointElementId = `toolbar-mount-point-${props.note.id}`;
		this.editorConfig = this.getEditorConfig();
		this.intersectionObserver = new IntersectionObserver(this.onScreenIntersection);
	}

	get meetingProvider(): ConferenceCallProvider {
		const { event } = this.getNote();
		if (event?.conferenceCalls.some(c => c.provider === "HANGOUTS_MEET")) return "HANGOUTS_MEET";
		if (event?.conferenceCalls.some(c => c.provider === "ZOOM")) return "ZOOM";
		return "UNKNOWN";
	}

	trackNoteHeaderHeight = (height: number) => {
		this.setState({ noteHeaderHeight: height });
	};

	trackSuggestedActionsHeight = (height: number) => {
		this.setState({ suggestedActionsHeight: height });
	};

	onScreenIntersection = (entries: IntersectionObserverEntry[]) => {
		// just one target at the moment
		if (entries.length !== 1) return;

		const isIntersecting = entries[0]["isIntersecting"];

		this.setState({ isOnScreen: isIntersecting });

		if (this.multicursor) {
			if (isIntersecting) {
				this.multicursor.onScreenVisible();
			} else {
				this.multicursor.onScreenHidden();
			}
		}
	};

	setAttachmentDropzoneRef = (ref: AttachmentsRelay) => {
		this.attachmentDropzoneRef = ref;
	};

	setSelectedRecordingId = (recordingId: string) => {
		this.setState({ selectedRecordingId: recordingId });
	};

	getEditorConfig(): EditorConfig {
		const note = this.getNote();
		const attendees = this.getMergedAttendees();

		const participants: readonly (
			| AttendeeAttrs
			| { id: string; email: string; firstName: string; fullName: string; image: string }
		)[] = attendees || note.noteuserSet.map(({ user }) => user);

		if (!this.collab) {
			this.collab = new Collab({
				documentId: this.props.note.id,
				sessionId: window.CLIENT_SESSION_ID,
				userId: note.viewerCanEdit ? decodeGlobalId(this.props.viewer.user.id)[1] : -1,
				initialData: note.rawDraftJson,
			});
			this.collab.addEventListener("statuschange", this.onEditorStatusChange as EventListener);
		}
		if (!this.externalAttribute) {
			this.externalAttribute = new ExternalAttribute({
				documentId: this.props.note.id,
				sessionId: window.CLIENT_SESSION_ID,
				initialData: note.rawDraftJson,
			});
		}
		if (!this.multicursor) {
			this.multicursor = new MultiCursor({
				documentId: note.id,
				sessionId: window.CLIENT_SESSION_ID,
				user: note.viewerCanEdit ? this.props.viewer.user : null,
				collab: this.collab,
				onUserPresenceChange: this.onUserPresenceChange,
			});
		}

		const markOptions = note.viewerCanEdit
			? { linkEditable: true, placeholderEditable: false, linkToolBarAllowEmbeds: true }
			: {
					linkEditable: false,
					placeholderEditable: false,
					linkToolBarAllowEmbeds: false,
					disableLinkToolbar: true,
					disablePlaceholderToolbar: true,
				};

		const extensions = [
			...getAllMarks(markOptions),
			new BlockActions(),
			new BlockBoundary(),
			// CollapseExtension must come after BlockBoundary since it uses
			// blockBoundary's plugin state to initialize its own
			new CollapseExtension(this.props.note.collapsedBlockKeys),
			new Keyboard(),
			new TextNode(),
			new Indent(),
			new Unstyled(),
			new BulletPoint(),
			new NumberedListItem(),
			new ImageStrip({
				contentId: this.props.note.id,
				disableWhenNotEditable: true,
				hidePreview: false,
				initialDimensions: this.props.note.attachments
					.filter(Boolean)
					.reduce((acc, attachment) => ({ ...acc, [attachment.id]: attachment.dimensions || undefined }), {}),
			}),
			new ImageStripItem(),
			new ImageStripItemFile(),
			new ImageStripItemCaption(),
			new HardBreak(),
			new HorizontalRule(),
			new HeaderNode({ viewer: this.props.viewer }),
			new AITopicsExtension({ noteId: this.props.note.id }),
			new Mention({ currentUserId: this.props.viewer.user.id }),
			new Tag({
				showToolbar: !this.props.viewer.user.isGuest,
				allowEditing: !this.props.viewer.user.isGuest,
				clickable: !this.props.viewer.user.isGuest,
			}),
			new CustomEmoji({ accountId: this.props.note.stream.account.id }),

			new Placeholder(),
			new DropTarget({
				// Last one wins
				handlers: [
					new NoteTaskDropHandler(),
					new MultiSelectDropHandler(),
					new FileDropHandler(this.onUploadFiles),
					new FellowAttachmentHandler(),
					new ImageStripItemHandler(),
				],
			}),
			...(note.viewerCanEdit ? [new MultiSelect()] : []),
			new MousePos(),
			new MoveSelection(),
			new ModS(),
			new FilePaste({ upload: this.onUploadFiles }),
			new BlockCreator({ userId: this.props.viewer.user.id }),
			new FocusArea({ getTarget: (dom: HTMLElement) => dom.closest(".notes-stream-card")?.querySelector(".main") }),
			new (withTimestampAttrs(Todo))({
				note: note,
				enableCopiedItemPopup: true,
				disableWhenNotEditable: true,
				viewerId: this.props.viewer.user.id,
				...(note.viewerCanEdit
					? {
							disabledReadOnly: false,
						}
					: {
							disabledReadOnly: true,
							readOnly: true,
						}),
			}),
			new Timestamp({ onTimestampClick: this.onTimestampClick }),
			new Agenda({
				note: note,
				enableCopiedItemPopup: true,
				employeeGuidanceShowManager: this.props.employeeGuidanceShowManager,
				viewerId: this.props.viewer.user.id,
				...(note.viewerCanEdit
					? {
							disabledReadOnly: false,
						}
					: {
							disabledReadOnly: true,
							readOnly: true,
						}),
			}),
			...(note.noteType == "goalset"
				? [
						new Goal({
							note: note,
							attendees: participants,
							viewerId: this.props.viewer.user.id,
						}),
					]
				: []),
			new TypeAhead({
				providers: [
					new SlashProvider({
						isExternalUser: this.props.note.stream.isExternal,
					}),
					new MentionProvider(),
					// new AssignProvider(),
					new EmojiProvider(),
					new TagProvider({ viewerIsGuest: this.props.viewer.user.isGuest }),
				],
			}),
			new ActionItemIntegrationsPlugin({
				noteId: this.props.note.id,
				initialData: this.props.note.integrationLinkedActionItems,
			}),
			new ActionItemProgressPlugin(),
			new Reactions({
				user: {
					id: this.props.viewer.user.id,
					firstName: this.props.viewer.user.firstName || this.props.viewer.user.email,
				},
				account: this.props.note.stream.account,
				noteId: this.props.note.id,
				initialData: JSON.parse(this.props.note.reactions),
			}),
			...(note.viewerCanEdit
				? [
						new Toolbar({
							setAttachmentsRef: ref => (this.attachmentsContainer = ref),
							isExternalUser: this.props.note.stream.isExternal,
							mountPointId: this.toolbarMountPointElementId,
						}),
					]
				: []),
			new Comments({
				noteId: this.props.note.id,
				initialData: this.props.note.comments,
			}),
			new AsanaItems({
				noteId: this.props.note.id,
				initialData: this.props.note.asanaActionItems,
			}),
			new Paste(),
			new PasteBuiltInEmoji(),
			new SelectedBlock(),
			new InputRules(),
			new Embed({ renderFrame: true }),
			new KeyResult(),
			this.collab,
			this.multicursor,
			this.externalAttribute,
			new BlockAnnotation(),
			...(this.props.viewer.account.loomEnabled ? [new RecordVideo()] : []),
			new PromptExtension(),
		];

		return new EditorConfig({
			extensions: extensions,
			nodes: {
				doc: {
					content: "block+",
				},
			},
			extraEditorProps: {
				// Keeps the selection from being obscured by the suggested actions.
				// The bottom value is a number that is larger than the height of suggested actions as of writing.
				scrollThreshold: { top: 0, bottom: 85, left: 0, right: 0 },
				scrollMargin: { top: 0, bottom: 85, left: 0, right: 0 },
			},
		});
	}

	componentDidUpdate(prevProps: Props, _prevState: State) {
		if (!this.props.note) return;
		if (this.props.inAgendaBuilderMode !== prevProps.inAgendaBuilderMode) {
			if (this.props.inAgendaBuilderMode) {
				this.generateAgendaBuilderHeaders();
			} else {
				this.onExitAgendaBuilderMode();
			}
		} else if (this.props.agendaBuilderModeRegenerateKey !== prevProps.agendaBuilderModeRegenerateKey) {
			this.generateAgendaBuilderHeaders();
		}

		if (!isEqual(this.props.note.comments, prevProps.note?.comments)) {
			eventEmitter.emit("NoteComments.dataUpdate", this.props.note.id, this.props.note.comments);
		}

		if (!isEqual(this.props.note.reactions, prevProps.note?.reactions)) {
			const reactionsExt = this.editorRef.current?.editorView?.extensions.get("block-reactions");
			if (reactionsExt instanceof Reactions) {
				reactionsExt.refreshReactionsData(JSON.parse(this.props.note.reactions));
			}
		}

		if (!isEqual(this.props.note.integrationLinkedActionItems, prevProps.note?.integrationLinkedActionItems)) {
			const linksExt = this.editorRef.current?.editorView?.extensions.get(actionItemIntegrationLinkProseKey);
			if (linksExt instanceof ActionItemIntegrationsPlugin) {
				linksExt.refreshIntegrationsData(this.props.note.integrationLinkedActionItems);
			}
		}

		if (!isEqual(this.props.note.asanaActionItems, prevProps.note.asanaActionItems)) {
			const asanaExt = this.editorRef.current?.editorView?.extensions.get(asanaItemsProseKey);
			if (asanaExt instanceof AsanaItems) {
				asanaExt.refreshAsanaItemsData(this.props.note.asanaActionItems);
			}
		}

		if (!this.props.note.hasMeetingRecordings && prevProps.note.hasMeetingRecordings) {
			// eslint-disable-next-line react/no-did-update-set-state
			this.setState({ currentTab: "notes" });
		}

		if (this.meetingRecapRef.current && this.state.forwardedRecapVideoState) {
			this.meetingRecapRef.current.updateVideoState(
				this.state.forwardedRecapVideoState.playing,
				this.state.forwardedRecapVideoState.currentTime,
			);
			// We just want to use the forwarded video state once and then discard it
			// eslint-disable-next-line react/no-did-update-set-state
			this.setState({ forwardedRecapVideoState: undefined });
		}
	}

	componentDidMount() {
		this._isMounted = true;

		if (this.containerRef.current) {
			this.intersectionObserver.observe(this.containerRef.current);
			// Used when we have the video for this note's recap in a mini player and we are closing it
			// without having the note's video available
			this.containerRef.current.addEventListener(RECORDING_SHOW_VIDEO, this.onShowRecap);
		}

		this.ensureSocket();
		this.getCrossWorkspaceFeatureFlag();
		eventEmitter.on("NoteComments.open", this.onShowCommentsMessage);
		if (this.props.initiallyHighlightedBlockKey && this.props.isNoteSpecifiedByUrl) {
			const { initiallyHighlightedBlockKey } = this.props;
			// TODO: deal with `null` returned by querySelector without timeout
			window.setTimeout(() => {
				this.ensureBlockAncestorsExpanded(initiallyHighlightedBlockKey);
				this.containerRef.current
					?.querySelector(`[data-block-id="${initiallyHighlightedBlockKey}"]`)
					?.scrollIntoView({ block: "center" });
			}, 100);
			this.highlightBlock(this.props.initiallyHighlightedBlockKey);
		}

		const { note, isFirstNote, location } = this.props;
		const { applyTemplateId, latestNoteId } = location.state || {};

		if (applyTemplateId && (isFirstNote || latestNoteId === note.id)) {
			// Just apply the template if the note is empty and we're coming from the template backend
			if (note.version === 0) {
				// Set it as default if no default is already set
				const makeDefault = !note.stream.template || !note.stream.templateHasBeenChanged;
				applyTemplate({
					noteId: note.id,
					templateId: applyTemplateId,
					makeDefault,
				});
				dispatchAnalyticsTrack("[Use Template] Template Applied", {
					templateId: applyTemplateId,
					fromTemplatesSection: true,
					autoApplied: true,
					makeDefault,
				});
				if (location.state) window.history.replaceState({}, "");
			} else {
				// Otherwise show the apply modal
				__globalModalContext
					?.open<boolean>(({ close }) => (
						<ApplyTemplateModal
							fromTemplatesSection
							noteId={note.id}
							noteVersion={note.version}
							templateId={applyTemplateId}
							close={close}
						/>
					))
					.then(() => {
						if (location.state) window.history.replaceState({}, "");
					});
			}
		}

		if (inVideocall) {
			window.addEventListener("message", this.onBrowserExtensionMessage);
		}
	}

	componentWillUnmount() {
		if (this.socket) {
			this.socket.off("message", this.onSocketMessage);
			this.socket.off("open", this.onSocketOpen);
			this.socket.close();
			this.socket = null;
		}

		this._isMounted = false;
		if (this.props.onComponentUnmount) {
			this.props.onComponentUnmount();
		}

		this.intersectionObserver.disconnect();

		// Update the rawDraftJson in the Note object to the latest version that is
		// confirmed by the server.
		if (this.editorRef.current?.editorView) {
			const { state } = this.editorRef.current.editorView;
			const collabState = CollabPluginKey.getState(state);
			if (collabState) {
				const { lastServerVersion, unconfirmedSteps } = collabState;
				const version = lastServerVersion;
				collabState.removeEventListener("statuschange", this.onEditorStatusChange as EventListener);

				// It's possible for the user to leave the page before their steps have
				// been saved (if they say OK to the warning we show). In that case, we
				// undo those pending changes before we store the doc in the relay store.
				const tr = [...unconfirmedSteps].reverse().reduce((tr, step) => tr.step(step.inverted), state.tr);
				const doc = tr.doc.toJSON();

				commitLocalUpdate(environment, store => {
					const note = store.get(this.props.note.id);

					// Either there's no node in the store for this note (unlikely), or
					// there have been no changes to the document. In either case we'll
					// just stop now.
					if (!note || version === this.props.note.version) {
						return;
					}

					// This hack sets the prototype of all node and mark `attrs` to be
					// Object (they are created with a `null` prototype). This is because
					// Relay uses a comparison function that depends on having a
					// `.hasOwnProperty` method (from Object). Unfortunately, prosemirror
					// creates `attrs` using `Object.create(null)`.
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					doc.content.forEach(function addAttrsProto(node: any) {
						if (node.attrs) Object.setPrototypeOf(node.attrs, Object.prototype);

						if (node.type === "text") {
							if (node.marks) node.marks.forEach(addAttrsProto);
						} else if (node.content) {
							node.content.forEach(addAttrsProto);
						}
					});

					// This song and dance is needed to set the value of a JSON scalar.
					// See issue: https://github.com/facebook/relay/issues/2441
					/* eslint-disable no-underscore-dangle */
					// @ts-ignore
					note._mutator.setValue(note._dataID, "rawDraftJson", { doc, version });
					/* eslint-enable no-underscore-dangle */

					note.setValue(version, "version");
				});
			}
		}
		eventEmitter.removeListener("NoteComments.open", this.onShowCommentsMessage);

		if (inVideocall) {
			window.removeEventListener("message", this.onBrowserExtensionMessage);
		}
	}

	ensureSocket = () => {
		const groupName = this.props.note.groupName;

		if (this.socket || !groupName) return;

		const websocketClient = WebsocketClient.getInstance();
		this.socket = websocketClient.getOrJoinGroup(groupName);
		this.socket.on("message", this.onSocketMessage);
		this.socket.on("open", this.onSocketOpen);
	};

	onSocketOpen = () => {
		this.updateGroupMetadata(false);
	};

	updateGroupMetadata = (override: boolean) => {
		const groupName = this.props.note.groupName;
		WebsocketClient.getInstance().updateGroupMetadata(groupName, override, {
			lastEntityId: Date.now().toString(),
		});
	};

	onSocketMessage = (payload: NoteSocketPayload) => {
		if (payload.type === "ACTION_ITEMS_CARRIED_FORWARD") {
			commitLocalUpdate(environment, store => {
				const note = store.get(this.props.note.id);
				note?.setValue(true, "actionItemsCarriedForward");
			});
		} else if (payload.type === "TALKING_POINTS_CARRIED_FORWARD") {
			commitLocalUpdate(environment, store => {
				const note = store.get(this.props.note.id);
				note?.setValue(true, "talkingPointsCarriedForward");
			});
		} else if (payload.type === "EVERYTHING_CARRIED_FORWARD") {
			commitLocalUpdate(environment, store => {
				const note = store.get(this.props.note.id);
				note?.setValue(true, "talkingPointsCarriedForward");
				note?.setValue(true, "actionItemsCarriedForward");
			});
		} else if (payload.type === "REACTIONS_CHANGED") {
			const reactionsExt = this.editorRef.current?.editorView?.extensions.get("block-reactions");
			if (reactionsExt instanceof Reactions) {
				reactionsExt.dispatchReactionUpdate(payload.data, true);
			}
		} else if (payload.type === "COMMENTS_CHANGED") {
			this.updateGroupMetadata(true);
			const commentsExt = this.editorRef.current?.editorView?.extensions.get("block-comments");
			if (commentsExt instanceof Comments) {
				commentsExt.dispatchCommentsUpdate(payload.data);
			}
		} else if (payload.type === "NOTE_UPDATED") {
			let meetingEnded = false;

			commitLocalUpdate(environment, store => {
				const note = store.get(this.props.note.id);
				for (const k in payload.data) {
					note?.setValue(payload.data[k as keyof typeof payload.data], k);

					if (k === "finishedAt") {
						meetingEnded = !!payload.data[k as keyof typeof payload.data];
					} else if (k === "isLocked" && payload.data[k] && this.props.inAgendaBuilderMode) {
						this.onExitAgendaBuilderMode();
					}
				}
			});

			if (meetingEnded) {
				this.onMeetingEnded();
			}
		} else if (
			payload.type === "ASANA_UPDATED" ||
			payload.type === "ASANA_COMPLETED" ||
			payload.type === "ASANA_ITEM_UNLINKED"
		) {
			const { type, data } = payload;
			const asanaUpdateData: AsanaItemsUpdateData = { ...data, updateType: type };
			const asanaItemsExt = this.editorRef.current?.editorView?.extensions.get("asana-items");
			if (asanaItemsExt instanceof AsanaItems) {
				asanaItemsExt.dispatchAsanaUpdate(asanaUpdateData).catch(console.error);
			}
		} else if (payload.type === "ACTION_ITEM_INTEGRATION_LINK_UPDATED") {
			const { data } = payload;
			const actionItemIntegrationPlugin = this.editorRef.current?.editorView?.extensions.get(
				actionItemIntegrationLinkProseKey,
			);
			if (actionItemIntegrationPlugin instanceof ActionItemIntegrationsPlugin) {
				actionItemIntegrationPlugin.dispatchActionItemIntegrationLinkUpdate(data).catch(console.error);
			}
		} else if (payload.type === "NOTE_CARRY_FORWARD") {
			this.refetchCurrentNote(); // Might limit to refetch only `reactions`, `comments`, and `actionItemIntegrationLink`
		} else if (payload.type === "AI_HEADERS_UPDATE") {
			if (payload.data.status === "DONE") {
				this.props.setDoneGeneratingAIHeaders?.(true);
			} else {
				const headers = [payload.data.headerData];
				// This signal may be sent at the same time that the headers mutation returns, so we need to dedupe
				this.setState(prevState => ({
					generatedHeaders: uniqBy([...prevState.generatedHeaders, ...headers], "id"),
				}));
			}
		}
	};

	getCrossWorkspaceFeatureFlag = async () => {
		const isCrossWorkspaceEnabled = await fetchFeature("cross_workspace_collaboration", "account");
		this.setState({ isCrossWorkspaceEnabled });
	};

	refetchCurrentNote = () => {
		this.props.relay.refetch({ id: this.props.note.id });
	};

	onDeleteExecuted = (type: DeletionType) => {
		if (this.props.viewer.user.isGuest) {
			return;
		}

		const note = this.props.note;
		if (type === "EVERYTHING") {
			this.props.history.push("/meetings");
		}
		if (type === "RECAP") {
			const { selectedRecordingId } = this.state;
			const firstOtherRecording = note?.meetingRecordings?.find(recording => recording.id !== selectedRecordingId);
			if (firstOtherRecording) {
				this.setState({ selectedRecordingId: firstOtherRecording.id });
			} else {
				this.setState({ selectedRecordingId: undefined, currentTab: "notes" });
			}

			this.refetchCurrentNote();
		}
	};

	openResolvedCommentsModal = () => {
		__globalModalContext?.open(({ close }) => <ResolvedModal noteId={this.props.note.id} onClose={close} />);
	};

	openEditEventModal = () => {
		const { event } = this.getNote();
		if (!event || event.start === null || !event.end) return;

		__globalModalContext?.open(({ close }) => (
			<EventFormModal
				viewer={this.props.viewer}
				onFormFinish={close}
				eventToEdit={{
					guid: event.guid,
					title: event.title,
					// It's not okay if event.start/end are null but the check above should prevent that
					start: event.start ? new Date(event.start) : new Date(),
					end: event.end ? new Date(event.end) : new Date(),
					isAllDay: event.isAllDay,
					type: event.type,
					calendar: event.calendar,
				}}
			/>
		));
	};

	onShowCommentsMessage = (noteId: string, blockKey: string, _options: { intendToAdd?: boolean } = {}) => {
		if (noteId !== this.props.note.id) return;
		this.disableFullscreen();
		if (window.innerWidth <= 734) {
			this.props.history.push(
				`${baseStreamUrl(this.props.match.params, this.props.viewer.user.isGuest)}comments/${toPathForTab({
					noteId,
					blockKey,
				})}`,
			);
		}
		const threadKey = threadKeyFromNoteIdAndBlockKey(noteId, blockKey);
		this.props.setCommentContext({ threadKey, noteId, blockId: blockKey });
	};

	onMeetingFeedbackClick = () => {
		const { viewer, note, onShowFeedbackContent } = this.props;

		if (viewer.account.billingAccount.hasAccessToMeetingFeedback) {
			this.openMeetingFeedbackModal({ streamId: note.stream.id, noteId: note.id, onShowFeedbackContent });
		} else {
			showFeatureModal("meeting_feedback");
		}
	};

	onMeetingEnded = () => {
		const { viewer, note, onShowFeedbackContent } = this.props;
		const hasAccessToMeetingFeedback = viewer.account.billingAccount.hasAccessToMeetingFeedback;
		const {
			id: noteId,
			viewerSentMeetingFeedback,
			stream: { id: streamId, isMeetingFeedbackPromptEnabled },
		} = note;

		if (viewerSentMeetingFeedback || !note.event || !isMeetingFeedbackPromptEnabled || !hasAccessToMeetingFeedback) {
			toast.success("Meeting ended");
		} else {
			this.openMeetingFeedbackModal({ streamId, noteId, onShowFeedbackContent, useAnimation: true });
		}
	};

	onBrowserExtensionMessage = (event: MessageEvent) => {
		const { viewer, note } = this.props;

		const hasAccessToMeetingFeedback = viewer.account.billingAccount.hasAccessToMeetingFeedback;
		const {
			id: noteId,
			eventStart,
			viewerSentMeetingFeedback,
			stream: { id: streamId, isMeetingFeedbackPromptEnabled },
		} = note;

		const isTodaysNote = eventStart ? moment().isSame(eventStart, "day") : false;

		// showCopilotPaywall
		if (
			isTodaysNote &&
			!viewerSentMeetingFeedback &&
			note.event &&
			isMeetingFeedbackPromptEnabled &&
			hasAccessToMeetingFeedback &&
			event.data.type === "BROWSER_EXTENSION_ACTION" &&
			event.data.action === "rateMeeting"
		) {
			this.openMeetingFeedbackModal({ streamId, noteId, initiallyLeftMeeting: true });
		} else if (
			note.event &&
			event.data.type === "BROWSER_EXTENSION_ACTION" &&
			event.data.action === "showCopilotPaywall"
		) {
			this.openCopilotPaywallModal();
		}
	};

	getOnCloseMeetingFeedbackModal = (callback: () => void) => () => {
		this.meetingFeedbackModalOpened = false;
		callback();
	};

	openMeetingFeedbackModal = (props: Omit<MeetingFeedbackModalProps, "close">) => {
		if (this.meetingFeedbackModalOpened) return;

		__globalModalContext?.open(({ close }) => {
			this.meetingFeedbackModalOpened = true;
			const onClose = this.getOnCloseMeetingFeedbackModal(close);

			return <MeetingFeedbackModal {...props} close={onClose} />;
		});
	};

	getOnCloseCopilotPaywallModal = (callback: () => void) => () => {
		this.copilotPaywallModalOpened = false;
		callback();
	};

	openCopilotPaywallModal = () => {
		if (this.copilotPaywallModalOpened) return;

		__globalModalContext?.open(({ close }) => {
			this.copilotPaywallModalOpened = true;
			const onClose = this.getOnCloseCopilotPaywallModal(close);

			return <OutOfCreditsModal onClose={onClose} />;
		});
	};

	setEventAsMeeting = async () => {
		const res = await warning(
			<div>
				If this recurring event is not a 1-on-1, Fellow can mark it as a regular meeting. Notes for future occurrences
				of this event will only appear in the meetings section.
			</div>,
			{ title: "Is this not a 1-on-1?", yesProps: { variant: "destructive" }, yesText: "Not a 1-on-1" },
		);

		if (!res) return;

		const note = this.getNote();
		dispatchAnalyticsTrack("[1on1s] split", { note_id: note.id }); // eslint-disable-line camelcase

		commitMutation<SplitMutationType>(environment, {
			mutation: splitMutation,
			variables: {
				input: {
					id: note.id,
					useForNewNotes: true,
				},
			},
			onCompleted: data => {
				const { splitNote } = data;
				if (splitNote && splitNote.ok) {
					toast.success(`Removed document from stream`);
					if (this.props.onSplitNote) {
						this.props.onSplitNote();
					}
				} else {
					toast.error(`Unable to remove document from stream`);
				}
			},
		});
	};

	onUnlinkMeetingNote = async () => {
		const res = await warning(<div>You&apos;ll still be able to access it from the meetings section.</div>, {
			title: "Are you sure you want to unlink this note from its stream?",
			yesProps: { variant: "destructive" },
			yesText: "Unlink",
		});

		if (!res) return;

		const note = this.getNote();
		dispatchAnalyticsTrack("[meetings] split", { note_id: note.id }); // eslint-disable-line camelcase

		commitMutation<SplitMutationType>(environment, {
			mutation: splitMutation,
			variables: {
				input: {
					id: note.id,
					useForNewNotes: false,
				},
			},
			onCompleted: (data, err) => {
				if (err) toast.error("Unable to unlink note from stream");

				const { splitNote } = data;
				if (splitNote && splitNote.ok) {
					toast.success("Unlinked note from stream");
					if (this.props.onSplitNote) {
						this.props.onSplitNote();
					}
				} else {
					toast.error("Unable to unlink note from stream");
				}
			},
		});
	};

	scrollIntoView() {
		const noteCard = this.containerRef.current;
		fastdom.measure(() => {
			const scrollContainer = noteCard?.closest(".scroll-container");
			if (noteCard && scrollContainer) {
				const headerHeight = parseInt(getComputedStyle(noteCard).getPropertyValue("--stream-header-height")) || 0;
				const noteCardOffset = Math.max(noteCard.offsetTop - headerHeight, 0);
				fastdom.mutate(() => {
					scrollContainer.scrollTo({
						top: noteCardOffset,
						behavior: "smooth",
					});
				});
			}
		});
	}

	private ensureBlockAncestorsExpanded(key: string): void {
		const view = this.editorRef.current?.editorView;
		if (!view) return;
		const blockBoundary = getBlockBoundaryState(view.state);
		if (!blockBoundary || !blockBoundary.blockBoundaries[key]) return;
		const [blockPos] = blockBoundary.blockBoundaries[key];
		const blockNode = view.state.doc.nodeAt(blockPos);
		if (!blockNode?.attrs.blockId) return;

		const collapsedAncestors = getCollapsedAncestors(view.state, blockNode);
		if (!collapsedAncestors.length) return;
		setCollapsedMany(view, collapsedAncestors, false);
	}

	highlightBlock(key: string) {
		const highlight = (remainingTries = 3) => {
			const block = this.containerRef.current?.querySelector(`[data-block-id="${key}"]`);
			if (block instanceof HTMLElement) {
				this.ensureBlockAncestorsExpanded(key);
				block.scrollIntoView({ behavior: "smooth", block: "center" });
				block.classList.add(styles.highlightedBlock);
				setTimeout(() => {
					block.classList.remove(styles.highlightedBlock);
				}, 5000);
			} else if (remainingTries > 0) {
				window.setTimeout(() => highlight(remainingTries - 1), 50);
			}
		};
		window.setTimeout(() => highlight(), 0);
	}

	openTranscriptModal(recordingId?: string, timestamp?: number): void {
		const note = this.getNote();
		openRecapModal({ noteId: note.id, recordingId, timestamp });
	}

	getNote = () => {
		const note = this.props.note;
		return {
			...note,
			users: note.noteuserSet.map(({ user }) => user),
		};
	};

	onGuidanceAdd = (topic: Suggestion) => {
		this.addTalkingPoints([
			// eslint-disable-next-line camelcase
			{ text: topic.topic, data: { assignee_id: this.props.viewer.user.id, guidanceId: topic.id } },
		]);
	};

	onGuidanceDismiss = (topic: Suggestion) => {
		commitMutation<DismissGuidanceType>(environment, {
			mutation: dismissGuidanceMutation,
			variables: {
				input: {
					notestreamId: this.props.note.stream.id,
					topicId: topic.id,
				},
			},
		});
	};

	getNoteTypeDisplay(note: Partial<Note>) {
		if (note.noteType === "oneonone") {
			return "1-on-1";
		}
		return "Document";
	}

	enableFullscreen = () => {
		this.setFullscreen(true);
	};

	disableFullscreen = () => {
		this.setFullscreen(false);
	};

	setFullscreen = (fullscreen: boolean) => {
		this.setState({ fullscreen });
	};

	openAttachmentDropzone = () => {
		this.setCurrentTab("notes");
		this.attachmentDropzoneRef?.open();
	};

	onUploadFiles = (files: File[]) => {
		dispatchAnalyticsTrack("[1on1s] Paste file");
		this.attachmentDropzoneRef?.upload(files);
	};

	onEditorStatusChange = (e: CustomEvent<StatusChange>) => {
		if (!this._isMounted) return;
		this.collabLastSaved = e.detail.lastSaveTime;
	};

	onUserPresenceChange = (presence: UserPresence) => {
		if (!this._isMounted) return;

		const { sessionId, user, lastActive, isCurrentlyViewing, platform } = presence;
		const noteUser = this.props.note.noteuserSet.find(nu => nu.user.email === user.email);

		if (!noteUser) {
			if (!user.isAnonymous) {
				return;
			}
			this.setState(({ anonymousAttendees }) => {
				let newAnonymousAttendees = [...anonymousAttendees];
				const isPresentInAnonymousList = anonymousAttendees.some(
					anonymousAttendee => anonymousAttendee.email === user.email,
				);
				if (!isCurrentlyViewing && isPresentInAnonymousList) {
					newAnonymousAttendees = anonymousAttendees.filter(
						anonymousAttendee => anonymousAttendee.email !== user.email,
					);
				} else if (isCurrentlyViewing && !isPresentInAnonymousList) {
					newAnonymousAttendees = anonymousAttendees.concat([
						{
							id: user.id,
							email: user.email,
							image: user.image ?? "",
							fullName: user.fullName,
							user: user,
							invitable: false,
							__typename: "Anonymous",
							isGuest: false,
							type: "GUEST",
							isAnonymous: true,
							isExternal: user.isExternal ?? false,
						},
					]);
				}
				return {
					anonymousAttendees: newAnonymousAttendees,
				};
			});
		}

		this.setState(({ attendeesPresenceInfo }) => {
			const userSessions = attendeesPresenceInfo[user.id];
			// new element first -- aiming `find(...)` finish earlier
			const newUserSessions = [
				{
					sessionId,
					isCurrentlyViewing: !!isCurrentlyViewing,
					lastActiveDatetime: lastActive ? moment(lastActive).format() : undefined,
					platform: platform,
				},
				...(userSessions ?? []).filter(s => s.sessionId !== sessionId),
			];

			return {
				attendeesPresenceInfo: {
					...attendeesPresenceInfo,
					[user.id]: newUserSessions,
				},
			};
		});
	};

	attendeesCurrentlyOnPlatform = () => {
		const { attendeesPresenceInfo } = this.state;
		return Object.entries(attendeesPresenceInfo).reduce((acc, [userId, presenceInfos]) => {
			const matchingPresence = presenceInfos.filter(p => p.isCurrentlyViewing && p.platform === this.meetingProvider);
			if (matchingPresence.length > 0) {
				acc[userId] = matchingPresence;
			}
			return acc;
		}, {} as AttendeesPresenceInfo);
	};

	addTalkingPoints = (talkingPoints: TalkingPoint[]) => {
		const blocksData = talkingPoints.map(({ text, data: { assignee_id: assignee, ...rest } }) => ({
			text,
			attrs: { assignees: assignee ? [{ id: assignee, status: NoteBlockStatus.NOT_DONE }] : [], ...rest },
			type: "agenda",
		}));
		commitMutation<AddBlocksMutationType>(environment, {
			mutation: addBlocksMutation,
			variables: {
				input: {
					noteId: this.props.note.id,
					blocksData,
				},
			},
		});
	};

	_mergedAttendeeCache = new Map<true | false, readonly AttendeeAttrs[]>();

	/**
	 * Get a list of users for the note, merged from the note's NoteUsers and, if applicable, the event's attendees.
	 *
	 * @param includeExternal Include users with email addresses from external domains, even if they haven't been
	 *                        invited as guests
	 */
	getMergedAttendees = (includeExternal = false) => {
		const { emailDomainList, isPersonalType } = this.props.viewer.account;

		const note = this.getNote();

		// Previous code in meetings/EventNotes
		const noteuserSet: AttendeeAttrs[] = note.noteuserSet.map(noteuser => ({
			user: noteuser.user,
			lastSeenAt: noteuser.lastSeenAt,
			invitable: false,
			...noteuser.user,
		}));

		const externalUsersSet: AttendeeAttrs[] =
			note.externalUsers?.map(externalUser => ({
				isExternalInNote: true,
				user: externalUser,
				invitable: !!externalUser.internalAttributes?.invitable,
				...externalUser,
			})) || [];

		// [FIXME] `AttendeeList`'s types require `AttendeeAttrs`, but are inaccurate.
		const attendees = note.stream.isExternal
			? // Don't show any attendees in the note header of external notes; the
				// event from graphql is from our account so the users in the attendees
				// can't actually gain access to this note without being explicitly
				// shared (since they are not from the same account as the note).
				//
				// FIXME: Maybe we could bring back this logic, but using the owner
				// account's email domains to decide if a user is external. We would
				// also need to remove the invite buttons in the note user list modal.
				[]
			: includeExternal
				? note.event?.attendees
				: note.event?.attendees.filter(
						attendee =>
							attendee && (emailDomainList.some(domain => attendee.email.endsWith(`@${domain}`)) || attendee.user),
					);
		const attendeesWithIsExternal =
			attendees?.map(attendee => {
				const isExternal = !isPersonalType && !emailDomainList.some(domain => attendee?.email.endsWith(`@${domain}`));
				const isExternalInvitingAllowed =
					this.props.viewer.account.allowExternalInviting && this.state.isCrossWorkspaceEnabled;
				return (
					attendee && {
						...attendee,
						isExternal: isExternal && isExternalInvitingAllowed,
						isGuest: isExternal && !isExternalInvitingAllowed,
					}
				);
			}) || [];
		const eventAttendees = compact(attendeesWithIsExternal) as AttendeeAttrs[];

		const mergedAttendees = noteuserSet.concat([...eventAttendees, ...externalUsersSet]);

		const newValue = uniqBy(
			uniqBy(mergedAttendees, item => item.email?.toLowerCase()),
			item => item?.user?.id || item.email?.toLowerCase(),
		);
		const oldValue = this._mergedAttendeeCache.get(includeExternal);
		if (!oldValue || !isEqual(oldValue, newValue)) {
			this._mergedAttendeeCache.set(includeExternal, newValue);
			return newValue;
		}
		return oldValue;
	};

	getEditorContext(newContext: EditorContext): EditorContext {
		if (!this.prevEditorContext || !isEqual(this.prevEditorContext, newContext)) {
			return (this.prevEditorContext = newContext);
		}
		return this.prevEditorContext;
	}

	getLastUpdatedString = (): string => {
		const note = this.getNote();
		const lastUpdatedDate = moment(this.collabLastSaved || note.updatedAt || "");
		return formatLastUpdatedDate(lastUpdatedDate);
	};

	onChange = (editorState: EditorState, contentChanged: boolean, isRemoteChange: boolean, oldState?: EditorState) => {
		if (contentChanged) this.props.onChange?.();

		// onChange seems to be triggered when the note is first loaded,
		// so we need to debounce this. By doing so, the notestream is
		// only watched when the user actually edits the document.
		if (!this.firstEditAlreadyHappened) {
			this.firstEditAlreadyHappened = true;
			return;
		}

		const note = this.getNote();
		if (
			contentChanged &&
			// can't watch goalset notestreams
			note.noteType !== "goalset" &&
			note.noteuserSet.length > 1 &&
			// user is not watching and has not unwatched
			note.stream.watching === null &&
			!this.props.viewer.user.isGuest &&
			this.props.viewer.settings?.automaticallyWatchNotestreams &&
			!this.alreadyTriedToWatch
		) {
			this.alreadyTriedToWatch = true;
			debouncedWatchStream(note.stream.id, true, { optimistic: true, allowErrorToast: false });
		}

		if (contentChanged && !this.state.noteHasBeenEdited) {
			let validNoteEdit = true;
			// This onChange isn't triggered by the application of the default template
			if (oldState && isRemoteChange) {
				// Determine whether this change is from the default template being cleared due to agenda builder
				const oldStateWasDefaultTemplate = noteContainsStreamTemplateOnly(oldState, note.stream);
				const defaultTemplateToBlankNoteFromAgendaBuilder =
					oldStateWasDefaultTemplate && noteIsEmpty(editorState) && this.props.inAgendaBuilderMode;

				// OR, if the change is from the default template being applied to the blank note due to agenda builder
				const newStateIsDefaultTemplate = noteContainsStreamTemplateOnly(editorState, note.stream);
				const blankNoteToDefaultTemplateFromAgendaBuilder =
					noteIsEmpty(oldState) && newStateIsDefaultTemplate && this.state.exitedAgendaBuilderMode;

				// Don't set noteHasBeenEdited if the change is from the default template being cleared when entering agenda builder
				// or if the change is from the default template being applied to the blank note on exiting agenda builder
				validNoteEdit = !(defaultTemplateToBlankNoteFromAgendaBuilder || blankNoteToDefaultTemplateFromAgendaBuilder);
			}

			if (validNoteEdit) {
				this.setState({ noteHasBeenEdited: true });

				// The condition for the "Prepare your first agenda" task in the welcome checklist is to edit a non-personal note.
				if (
					this.props.viewer.welcomeChecklist.firstAgendaPrepared === "INCOMPLETE" &&
					note.stream.type !== "personal"
				) {
					commitLocalUpdate(environment, store => {
						const welcomeChecklistRecord = store.get("client:viewer:welcomeChecklist");
						if (!welcomeChecklistRecord) return;

						const firstAgendaPreparedTaskKey = "FIRST_AGENDA_PREPARED" as const satisfies WelcomeChecklistTaskKey;
						welcomeChecklistRecord.setValue("PENDING_COMPLETION", `task(key:"${firstAgendaPreparedTaskKey}")`);
					});
				}
			}

			if (!isRemoteChange) {
				trackConversion(AGENDA_BUILDER_FEATURE_FLAG);
			}
		}

		if (contentChanged && inCalendarMode) {
			this.debouncedUpdateExtension();
		}
	};

	updateExtension() {
		window.parent.postMessage(
			{
				type: "EXTENSION_REFETCH_DATA",
			},
			"*",
		);
	}

	_momentCache: { [k: string]: ReturnType<typeof moment> } = {};
	/**
	 * Get a cached moment object by name. If the result of calling moment with
	 * `arg` has changed, the new value is cached and returned.
	 *
	 * Don't call this with dynamic keys; it will leak memory.
	 */
	getCachedMoment(name: string, arg: Parameters<typeof moment>[0]): ReturnType<typeof moment> {
		const newMoment = moment(arg);

		if (!(name in this._momentCache) || !newMoment.isSame(this._momentCache[name])) {
			return (this._momentCache[name] = newMoment);
		}

		return this._momentCache[name];
	}

	onOpenAgendaGeneratorPreview = () => {
		__globalModalContext?.open(({ close }) => (
			<AgendaGeneratorPreview note={this.props.note} viewer={this.props.viewer} close={close} />
		));
	};

	onExitAgendaBuilderMode = () => {
		const view = this.editorRef.current?.editorView;
		if (view && noteIsEmpty(view.state)) {
			// If the document is empty, apply the default template
			this.setState({ exitedAgendaBuilderMode: true }, () => {
				if (this.props.note.stream.template) {
					applyTemplate({
						noteId: this.props.note.id,
						templateId: this.props.note.stream.template.id,
						makeDefault: false,
						bypassNoteActivitySignals: true,
					});
				}
			});
		}

		this.setState({
			generatedHeaders: [],
			agendaBuilderError: false,
		});

		this.props.setInAgendaBuilderMode(false);
	};

	enterAgendaBuilderMode = () => {
		this.props.setInAgendaBuilderMode(true);
	};

	generateAgendaBuilderHeaders = () => {
		this.props.setDoneGeneratingAIHeaders?.(false);
		this.setState({
			generatedHeaders: [],
			agendaBuilderError: false,
		});

		const view = this.editorRef.current?.editorView;
		invariant(view);
		const aiTopicsPlugin = view.extensions.get(aiProseKey);
		invariant(aiTopicsPlugin instanceof AITopicsExtension);
		aiTopicsPlugin.debouncedCalculateAITopicsState(false);

		commitMutation<GenerateAgendaHeadersType>(environment, {
			mutation: generateAgendaHeadersMutation,
			variables: { noteId: this.props.note.id },
			onCompleted: ({ generateAgendaHeaders }, err) => {
				// It's useful to still return all the headers from the mutation,
				// to act as a catch-all in case something happens with the websocket signals

				const headers = generateAgendaHeaders?.headers ?? [];

				// Not sure why this doesn't show up in onError
				if (err) {
					this.setState({
						agendaBuilderError: true,
					});
					this.props.setInAgendaBuilderMode(false);

					throw new Error(err?.[0].message ?? "Error while generating your agenda. Please try again.");
				}

				if (headers.length > 0) {
					// There's a chance we'll receive a ws signal with header info at the same time as setting
					// the state here, so we need to manually combine and de-duplicate the headers
					this.setState(prevState => ({
						generatedHeaders: uniqBy([...prevState.generatedHeaders, ...headers], "id"),
					}));
				}
				this.props.setDoneGeneratingAIHeaders?.(true);
			},
		});
	};

	setCurrentTab = (tab: CurrentTabType) => {
		if (this.state.currentTab === "recap" && tab !== "recap") {
			this.meetingRecapRef.current?.onLeaveRecapTab();
		}
		this.setState({ currentTab: tab });
	};

	onTimestampClick = (timestamp: number, recordingId?: string) => {
		onTimestampClickUtil(
			this.props.note.meetingRecordings?.map(recording => recording.id) ?? [],
			this.setSelectedRecordingId,
			this.videoPlayerRef,
			this.chaptersRef,
			timestamp,
			recordingId,
			this.setCurrentTab,
		);
	};

	onShowRecap = (event: RecordingShowVideoEvent) => {
		this.setCurrentTab("recap");
		this.setState({
			forwardedRecapVideoState: event.detail,
		});
	};

	onAgendaBuilderSettingsClicked = () => {
		this.props.onAgendaBuilderSettings?.();
	};

	stickyNoteBannersHeightChanged = (height: number) => {
		this.setState({ stickyNoteBannersHeight: height });
	};

	render() {
		if (!this.props.note) return null;
		const note = this.getNote();
		const billing = {
			hasAccessToDuedates: this.props.viewer.account.billingAccount.hasAccessToDuedates,
			hasAccessToJiraIntegration: this.props.viewer.account.billingAccount.hasAccessToJiraIntegration,
			hasAccessToComments: this.props.viewer.account.billingAccount.hasAccessToComments,
			hasAccessToEmbeds: this.props.viewer.account.billingAccount.hasAccessToEmbeds,
			hasAccessToAsana: this.props.viewer.account.billingAccount.hasAccessToAsana,
			hasAccessToObjectives: this.props.viewer.account.billingAccount.hasAccessToObjectives,
			hasAccessToMultipleAssignees: this.props.viewer.account.billingAccount.hasAccessToMultipleAssignees,
			featureAccess: this.props.viewer.account.billingAccount.featureAccess,
		};

		const jira = this.props.viewer.account.integrations.jira || undefined;

		const asanaIsConnected = this.props.viewer.integrations.asana?.isConnected;

		const connectedActionItemIntegrations = this.props.viewer.connectedActionItemIntegrations;

		const isAccountAdmin = !!this.props.viewer.user.internalAttributes?.isAccountAdmin;

		const attendees = this.getMergedAttendees();

		const participants: readonly (AttendeeAttrs | Note["noteuserSet"][0]["user"])[] =
			attendees || note.noteuserSet.map(({ user }) => user);
		const attendeesForHeader = attendees.filter(a => !a.isExternalInNote);

		const noteStartTime = this.getCachedMoment("noteStartTime", note.eventStart || "");
		const noteEndTime = this.getCachedMoment("noteEndTime", note.eventEnd || "");

		note.linkedActionItems.forEach(item => {
			if (!item.hasOriginalBlock || !item.originalBlockId || !item.originalBlock?.note) return;
			jumpInfoCache.set(item.originalBlockId, {
				url: item.originalBlock?.note?.stream.url ?? undefined,
				noteId: item.originalBlock?.note.id,
				blockKey: item.originalBlock?.key,
			});
		});

		const isAgendaBuilderFeatureFlagEnabled = growthbook.isOn(AGENDA_BUILDER_FEATURE_FLAG);

		// We show the agenda generator if:
		// - The feature flag is on
		// - The note belongs to a meeting/oneonone
		// - User haven't dismissed the banner
		// - The stream has no default template set or it's the default that was
		//   set when it was originally created.
		// - The note has yet to be edited
		// - User is not a guest
		// - Stream is not external
		// - Note is not locked
		const isAgendaGeneratorFeatureFlagEnabled = growthbook.isOn("ai-generate-agenda");
		const shouldShowAgendaGenerator = Boolean(
			isAgendaGeneratorFeatureFlagEnabled &&
				this.props.viewer.account.allowAiFeatures &&
				!note.viewerAgendaGeneratorBannerDismissed &&
				(note.noteType === "meeting" || note.noteType === "oneonone") &&
				(!note.stream.template || !note.stream.templateHasBeenChanged) &&
				attendees.length > 1 &&
				!note.stream.isExternal &&
				!this.context &&
				note.version === 0 &&
				!this.state.noteHasBeenEdited &&
				!inCalendarMode &&
				!this.props.viewer.user.isGuest &&
				!note.isLocked,
		);

		const shouldShowAutomationTimeSaverBanner = Boolean(
			!this.props.inAgendaBuilderMode &&
				noteStartTime.isValid() &&
				noteStartTime.isAfter(moment()) &&
				!this.props.viewer.user.isGuest &&
				!this.state.noteHasBeenEdited &&
				note.viewerShowAutomationTimeSaverBanner &&
				note.event &&
				!note.isLocked &&
				!shouldShowAgendaGenerator,
		);

		const shouldShowAutomationTimeSaverIllustration = Boolean(
			!this.props.inAgendaBuilderMode && this.props.showAutomationTimeSaverIllustration && !note.isLocked,
		);

		const shouldHideSuggestedActions = note.stream.displayName === "Example Meeting (by Fellow)";
		const shouldShowFloatingRecordingControls =
			this.props.viewer.hasAccessToCopilot && growthbook.isOn(FLOATING_RECORDING_CONTROLS_FEATURE);

		const suggestedActionAndReactions = (
			<>
				{shouldShowFloatingRecordingControls && (
					<FloatingRecordingControls
						viewer={this.props.viewer}
						note={this.props.note}
						isFirstNote={this.props.isFirstNote}
						eventGuid={note.eventGuid}
					/>
				)}
				{!shouldHideSuggestedActions && (
					<SuggestedActions
						viewer={this.props.viewer}
						note={this.props.note}
						noteGroupName={this.props.note.groupName}
						openMeetingFeedbackModal={this.onMeetingFeedbackClick}
						onHeightChanged={this.trackSuggestedActionsHeight}
					/>
				)}
				<div className={styles.liveReactionContainer}>
					<LiveReactionContainer
						noteContainerRef={this.containerRef}
						noteGroupName={this.props.note.groupName}
						viewerKey={this.props.viewer}
					/>
				</div>
			</>
		);

		const isShowingNotesTab = this.state.currentTab === "notes";
		const isShowingRecapTab = !isShowingNotesTab;

		const noteContent = (
			<ViewportContext.Consumer>
				{ctx => (
					<article
						style={{
							"--NoteHeaderHeight": `${this.state.noteHeaderHeight}px`,
							"--NoteStickyBannersHeight": `${this.state.stickyNoteBannersHeight}px`,
							"--SuggestedActionsHeight": `${this.state.suggestedActionsHeight}px`,
						}}
						data-type="note-content"
						className={styles.noteContent}
					>
						{this.state.isOnScreen && <SetOuterObjectContext objectId={note.id} />}

						<NoteHeader
							viewerKey={this.props.viewer}
							noteKey={this.props.note}
							trackNoteHeaderHeight={this.trackNoteHeaderHeight}
							embedded={this.props.embedded}
							onFullscreenClick={
								ctx.isComputer && !isNative && !this.state.fullscreen ? this.enableFullscreen : undefined
							}
							onAttachFilesClick={this.openAttachmentDropzone}
							onDeleteExecuted={this.onDeleteExecuted}
							onNotOneononeClick={note.eventGuid && note.noteType === "oneonone" ? this.setEventAsMeeting : undefined}
							onUnlinkClick={
								this.props.showUnlinkNote && note.event && note.noteType !== "oneonone"
									? this.onUnlinkMeetingNote
									: undefined
							}
							onResolvedCommentsClick={this.openResolvedCommentsModal}
							onAgendaGeneratorClick={this.onOpenAgendaGeneratorPreview}
							onGeneratedHeadingFlowClick={
								// This is for the agenda builder project.
								// We don't want to show the feature alongside the agenda generator project (i.e. the smart fellow project)
								!isAgendaGeneratorFeatureFlagEnabled &&
								isAgendaBuilderFeatureFlagEnabled &&
								this.props.viewer.account.allowAiFeatures &&
								note.eventGuid &&
								!this.props.inAgendaBuilderMode
									? this.enterAgendaBuilderMode
									: undefined
							}
							inAgendaBuilderMode={this.props.inAgendaBuilderMode}
							printProps={this.props.printProps}
							attendees={attendeesForHeader}
							anonymousAttendees={this.state.anonymousAttendees}
							attendeesPresenceInfo={this.state.attendeesPresenceInfo}
							recordingId={this.state.selectedRecordingId}
						/>

						<MeetingContainerTabs
							currentTab={this.state.currentTab}
							setCurrentTab={this.setCurrentTab}
							noteKey={note}
						/>

						<div className={cx({ [styles.hidden]: isShowingRecapTab })}>
							{shouldShowAutomationTimeSaverIllustration && <TimeSaver streamId={note.stream.id} />}

							{shouldShowAgendaGenerator && <AgendaGenerator note={this.props.note} viewer={this.props.viewer} />}

							<StickyNoteBanners onHeightChanged={this.stickyNoteBannersHeightChanged}>
								<>
									{shouldShowAutomationTimeSaverBanner &&
										(note.event?.isOrganizer ? (
											<CancelMeeting noteId={note.id} minutes={noteEndTime.diff(noteStartTime, "minutes")} />
										) : (
											<RequestCancelMeeting noteId={note.id} />
										))}

									{this.props.inAgendaBuilderMode && (
										<AgendaBuilderBanner
											onSettingsClick={this.onAgendaBuilderSettingsClicked}
											onClose={this.onExitAgendaBuilderMode}
										/>
									)}
									{this.state.agendaBuilderError && this.state.generatedHeaders.length === 0 && (
										<ErrorBanner onClose={this.onExitAgendaBuilderMode} onRetry={this.generateAgendaBuilderHeaders} />
									)}
								</>
							</StickyNoteBanners>

							{note.viewerCanEdit ? (
								note.isLocked ? (
									<div className={styles.toolbarContainer}>
										<NoteLocked canUnlockNote={note.canUnlockNote} />
									</div>
								) : null
							) : (
								<div className={styles.toolbarContainer}>
									<NoteReadOnly />
								</div>
							)}

							<div
								id={this.toolbarMountPointElementId}
								className={cx(styles.toolbarContainer, { [styles.isHidden]: note.isLocked })}
							/>

							<div data-tour-tooltip-target="agenda-editor" className={styles.editor}>
								<DismissibleElementsContext.Consumer>
									{dismissibleElementsContext => (
										<ProseReact
											enableReactHost
											className={styles.redesignedNoteProseWrapper}
											editable={!note.isLocked && note.viewerCanEdit}
											ref={this.editorRef}
											lockedNote={note.isLocked || !note.viewerCanEdit}
											onChange={this.onChange}
											context={this.getEditorContext({
												tags: this.props.viewer.account.tags,
												participants: sortBy(participants, p => p.id),
												noteId: note.id,
												noteUrl: note.url,
												refetchNote: this.refetchCurrentNote,
												noteEventGuid: note.eventGuid,
												isTeams: !!this.context,
												locked: note.isLocked,
												billing,
												jira,
												asanaIsConnected,
												connectedActionItemIntegrations,
												isAccountAdmin,
												teamsContext: this.context,
												userId: this.props.viewer.user.id,
												streamId: this.props.note.stream.id,
												streamName: this.props.note.stream.displayName,
												streamType: this.props.note.stream.type,
												streamIsExternal: this.props.note.stream.isExternal,
												streamHasExternalUsers: this.props.note.stream.hasExternalUsers,
												hasAccessToOriginalBlock: fromEntries(
													compact(
														note.linkedActionItems
															.filter(item => item.originalBlock)
															.map(
																item =>
																	item.hasOriginalBlock &&
																	item.originalBlockId && [item.originalBlockId, item.originalBlock != null],
															),
													),
												),
												hasAccessToVersioning: this.props.viewer.account.billingAccount.hasAccessToVersioning,
												hasAccessToNoteLocking: this.props.viewer.account.billingAccount.hasAccessToNoteLocking,
												hasAccessToPromptHeadings: this.props.viewer.account.billingAccount.hasAccessToPromptHeadings,
												allowCompleteByAny: this.props.viewer.account.allowCompleteByAny,
												getLastUpdatedString: this.getLastUpdatedString,
												onMeetingFeedbackClick:
													note.stream.isMeetingFeedbackPromptEnabled && note.eventGuid && !inVideocallMode
														? this.onMeetingFeedbackClick
														: undefined,
												meetingFeedbackSent: note.viewerSentMeetingFeedback,
												meetingFeedbackResponseCount: note.meetingFeedbackResponseCount,
												isFirstNote: this.props.isFirstNote,
												isMobile: ctx.isMobile,
												isDarkMode: this.props.isDarkMode,
												generatedHeaders: this.state.generatedHeaders,
												doneGeneratingHeaders: this.props.doneGeneratingAIHeaders,
												inAgendaBuilderMode: this.props.inAgendaBuilderMode,
												agendaBuilderTone: this.props.note.stream.agendaBuilderTone,
												agendaBuilderContext: this.props.note.stream.agendaBuilderContext,
												dismissibleElementsContext,
											})}
											initialDoc={note.rawDraftJson.doc}
											config={this.editorConfig}
											user={this.props.viewer.user}
										/>
									)}
								</DismissibleElementsContext.Consumer>
							</div>

							{this.attachmentsContainer &&
								!note.isLocked &&
								ReactDOM.createPortal(
									<Attachments
										componentRef={this.setAttachmentDropzoneRef}
										groupId={this.props.note.id}
										note={this.props.note}
									/>,
									this.attachmentsContainer,
								)}
						</div>

						<div className={cx({ [styles.hidden]: isShowingNotesTab })}>
							<MeetingRecap
								noteKey={note}
								viewerKey={this.props.viewer}
								allowRecapDownload={this.props.viewer.account.allowRecapDownload}
								videoPlaybackSettingsKey={this.props.viewer.videoPlaybackSettings}
								ref={this.meetingRecapRef}
								containerRef={this.containerRef}
								selectedRecordingId={this.state.selectedRecordingId}
								setSelectedRecordingId={this.setSelectedRecordingId}
								videoPlayerRef={this.videoPlayerRef}
								chaptersRef={this.chaptersRef}
								onTimestampClick={this.onTimestampClick}
							/>
						</div>

						{suggestedActionAndReactions}
					</article>
				)}
			</ViewportContext.Consumer>
		);

		const paywallNoteCreationDate = !noteStartTime.isValid()
			? this.getCachedMoment("paywallNoteCreationDate", note.createdAt ?? "")
			: noteStartTime;

		return (
			<>
				<NoteTemplateModalOpener ref={this.noteTemplateModal} />

				{this.props.employeeGuidance && (
					<EmployeeGuidance
						guidance={this.props.employeeGuidance}
						onAdd={this.onGuidanceAdd}
						onDismiss={this.onGuidanceDismiss}
						showManager={this.props.employeeGuidanceShowManager}
						className={styles.employeeGuidance}
					/>
				)}

				<MeetingPhaseContextProvider viewer={this.props.viewer} note={this.props.note}>
					<AskFellowActionProvider note={this.props.note} noteId={this.props.note.id}>
						<div
							ref={this.containerRef}
							className={cx(styles.noteCard, {
								[styles.isolatedForPrinting]: this.props.printProps.isIsolatedNoteCard,
								[styles.hiddenForPrinting]: this.props.printProps.inPrintNodeMode,
								["isolated-for-printing"]: this.props.printProps.isIsolatedNoteCard,
								["hidden-for-printing"]: this.props.printProps.inPrintNodeMode,
							})}
							style={this.props.style}
						>
							<Fullscreen
								className={cx(styles.fullscreenContainer, { [styles.fullscreenActive]: this.state.fullscreen })}
								enabled={this.state.fullscreen}
								onChange={this.setFullscreen}
							>
								<NoteAndFeedbackHistoryPaywall
									disabled={
										(note.noteType == "personal" && note.noteuserSet.length === 1) ||
										note.stream.fromSignupForViewer ||
										note.stream.hasExternalUsers
									}
									viewer={this.props.viewer}
									feature="note"
									text="notes that were created"
									creationDate={paywallNoteCreationDate}
								>
									{noteContent}
								</NoteAndFeedbackHistoryPaywall>
							</Fullscreen>
						</div>
					</AskFellowActionProvider>
				</MeetingPhaseContextProvider>
			</>
		);
	}
}

export default withProfiler(withRouter(smart(DumbNoteCard)), { name: "NoteCard" });
